import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Hotel, BadgePercent, CreditCard, CheckCircle, MapPin, Calendar, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { result, searchParams } = location.state || {};

  const [guestData, setGuestData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: ''
  });
  const [loading, setLoading] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <p className="text-gray-600 mb-4">No booking data found</p>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </Card>
      </div>
    );
  }

  const handleBooking = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const bookingData = {
        property_id: result.property.id,
        room_type_id: result.room.id,
        guest_name: guestData.name,
        guest_email: guestData.email,
        guest_phone: guestData.phone,
        check_in: searchParams.checkIn,
        check_out: searchParams.checkOut,
        num_guests: parseInt(searchParams.guests),
        price_per_night: result.price_per_night
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingId(response.data.id);
      setBookingConfirmed(true);
      toast.success('Booking confirmed successfully!');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to complete booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-6">
        <Card className="max-w-2xl w-full p-12 text-center" data-testid="booking-confirmation">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Confirmed!</h2>
          <p className="text-gray-600 mb-8">
            Your reservation at <span className="font-semibold">{result.property.name}</span> is confirmed.
            Confirmation details have been sent to <span className="font-semibold">{guestData.email}</span>.
          </p>
          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <p className="text-sm text-gray-600 mb-2">Booking ID</p>
            <p className="font-mono font-semibold text-gray-900" data-testid="booking-id">{bookingId}</p>
          </div>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/')} variant="outline">
              Back to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="relative w-11 h-11 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md">
            <Hotel className="w-6 h-6 text-white" />
            <div className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-sm border-2 border-white">
              <BadgePercent className="w-3.5 h-3.5 text-white" />
            </div>
          </div>
          <h1 className="text-lg font-bold text-gray-900">traveldiscount.ai</h1>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">Complete Your Booking</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleBooking} className="space-y-6">
              {/* Guest Information */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Guest Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Full Name</label>
                    <Input
                      placeholder="John Doe"
                      value={guestData.name}
                      onChange={(e) => setGuestData({...guestData, name: e.target.value})}
                      required
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Email</label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={guestData.email}
                      onChange={(e) => setGuestData({...guestData, email: e.target.value})}
                      required
                      data-testid="guest-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Phone</label>
                    <Input
                      type="tel"
                      placeholder="+39 123 456 7890"
                      value={guestData.phone}
                      onChange={(e) => setGuestData({...guestData, phone: e.target.value})}
                      required
                      data-testid="guest-phone-input"
                    />
                  </div>
                </div>
              </Card>

              {/* Payment Information (Mock) */}
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-900">Payment Information</h3>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Demo Mode:</strong> This is a mock payment interface. No real charges will be made.
                  </p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Card Number</label>
                    <Input
                      placeholder="1234 5678 9012 3456"
                      value={paymentData.cardNumber}
                      onChange={(e) => setPaymentData({...paymentData, cardNumber: e.target.value})}
                      data-testid="card-number-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Cardholder Name</label>
                    <Input
                      placeholder="John Doe"
                      value={paymentData.cardName}
                      onChange={(e) => setPaymentData({...paymentData, cardName: e.target.value})}
                      data-testid="card-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">Expiry</label>
                      <Input
                        placeholder="MM/YY"
                        value={paymentData.expiry}
                        onChange={(e) => setPaymentData({...paymentData, expiry: e.target.value})}
                        data-testid="card-expiry-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">CVV</label>
                      <Input
                        placeholder="123"
                        value={paymentData.cvv}
                        onChange={(e) => setPaymentData({...paymentData, cvv: e.target.value})}
                        data-testid="card-cvv-input"
                      />
                    </div>
                  </div>
                </div>
              </Card>

              <Button 
                type="submit" 
                className="w-full h-12 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="confirm-booking-btn"
              >
                {loading ? 'Processing...' : `Confirm & Pay €${result.total_price}`}
              </Button>
            </form>
          </div>

          {/* Booking Summary */}
          <div>
            <Card className="p-6 sticky top-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Booking Summary</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-900">{result.property.name}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    {result.property.city}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-1">Room Type</p>
                  <p className="font-semibold text-gray-900">{result.room.name}</p>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-600">Check-in</span>
                  </div>
                  <p className="font-semibold text-gray-900">{searchParams.checkIn}</p>
                  <div className="flex items-center gap-2 text-sm mt-3 mb-2">
                    <Calendar className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-600">Check-out</span>
                  </div>
                  <p className="font-semibold text-gray-900">{searchParams.checkOut}</p>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <Users className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-600">Guests</span>
                  </div>
                  <p className="font-semibold text-gray-900">{searchParams.guests}</p>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">€{result.price_per_night} × {result.nights} nights</span>
                    <span className="font-semibold text-gray-900">€{result.total_price}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-blue-600">€{result.total_price}</span>
                  </div>
                  <div className="savings-badge w-full text-center">
                    You save {result.savings_percent}%
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCheckout;